import {Injectable} from '@angular/core';
import {ApiFolderService} from '../sp-api/sp-api-folder/api-folder.service';
import {ApiPlatformFoldersService} from '../sp-api/sp-api-platform-folders/api-platform-folders.service';
import {AnyFolder, ProjectManagerService} from '../sp-project/project-manager.service';
import {Folder} from '../../models/folder';
import {isPlatformFolder} from '../../models/platform-folder';
import {Project} from '../../models/project';

/**
 * Every change to a folder, wherever the folder is kept (US-1033): a folder the
 * Audiense platform keeps changes through its Folders API, a Soprism folder
 * (a Custom Folder, or any folder while `di_platform_folders_enabled` is off)
 * through core-api, as before. Users manage both the same way in the dashboard.
 *
 * Each change also updates the folder the dashboard holds, so the list shows it
 * without a reload.
 */
@Injectable({
  providedIn: 'root'
})
export class FolderActionsService {
  constructor(
    private readonly soprism: ApiFolderService,
    private readonly platform: ApiPlatformFoldersService,
    private readonly projectManager: ProjectManagerService
  ) {}

  /** A new regular folder: on the platform with the flag on, in Soprism otherwise. */
  async createFolder(name: string): Promise<AnyFolder> {
    if (await this.projectManager.foldersOnPlatform()) return this.platform.createFolder(name);
    return this.soprism.postOne(new Folder(name));
  }

  async renameFolder(folder: AnyFolder, name: string): Promise<void> {
    if (isPlatformFolder(folder)) {
      await this.platform.renameFolder(folder.id, name);
      folder.name = name;
      return;
    }
    folder.name = name;
    await this.soprism.updateOne(folder);
  }

  async deleteFolder(folder: AnyFolder): Promise<void> {
    if (isPlatformFolder(folder)) {
      await this.platform.deleteFolder(folder.id);
      return;
    }
    await this.soprism.deleteOne(folder);
  }

  /** `justCreated`: the project was created a moment ago and may not be listed on the platform yet. */
  async addProject(folder: AnyFolder, project: Project, options: {justCreated?: boolean} = {}): Promise<void> {
    if (isPlatformFolder(folder)) {
      await this.platform.addProject(folder.id, project.id, {waitUntilListed: !!options.justCreated});
      folder.projects = [...folder.projects, {id: project.id, name: project.name}];
      return;
    }
    await this.soprism.projectAdd(folder as Folder, project);
    folder.projects = [...folder.projects, project];
  }

  async removeProject(folder: AnyFolder, project: Project): Promise<void> {
    if (isPlatformFolder(folder)) {
      await this.platform.removeProject(folder.id, project.id);
    } else {
      await this.soprism.projectRemove(folder as Folder, project);
    }
    folder.projects = folder.projects.filter((p: {id: number}) => p.id !== project.id);
  }
}
